import { CameraViewport, TileInfo, LODLevel, CubeFace } from '../Types';
import { ViewportCalculator } from '../ViewportCalculator';
import { TIMING, LOADING, ALL_CUBE_FACES } from './constants';
import { UrlBuilder } from './UrlBuilder';
import { throttle } from './utils';

/**
 * Viewport change event data
 */
export interface ViewportChangeEvent {
  viewport: CameraViewport;
  timestamp: number;
  timeSinceLastUpdate: number;
}

/**
 * Visible tiles computation result
 */
export interface VisibleTilesResult {
  fovTiles: TileInfo[];
  nonFovTiles: TileInfo[];
  fovFaces: Set<string>;
  totalTiles: number;
}

/**
 * Events emitted by ViewportService
 */
export interface ViewportServiceEvents {
  viewportChanged: (event: ViewportChangeEvent) => void;
  viewportStabilized: (viewport: CameraViewport) => void;
}

/**
 * Viewport service statistics
 */
export interface ViewportServiceStats {
  currentViewport: CameraViewport | null;
  lastUpdateTime: number;
  timeSinceLastUpdate: number;
  totalUpdates: number;
  throttledUpdates: number;
  stabilizationTime: number;
}

/**
 * Manages camera viewport state and visible tile computation
 */
export class ViewportService {
  private currentViewport: CameraViewport | null = null;
  private lastViewportUpdate = 0;
  private readonly updateThrottle: number;
  private readonly tilePadding: number;
  
  // Statistics
  private totalUpdates = 0;
  private throttledUpdates = 0;
  
  private readonly eventHandlers = new Map<keyof ViewportServiceEvents, Set<Function>>();
  
  // Throttled update function
  private readonly throttledUpdate: (viewport: CameraViewport) => void;

  constructor(
    updateThrottle: number = TIMING.VIEWPORT_UPDATE_THROTTLE,
    tilePadding: number = 1
  ) {
    this.updateThrottle = updateThrottle;
    this.tilePadding = tilePadding;
    
    // Create throttled update function
    this.throttledUpdate = throttle(
      (viewport: CameraViewport) => this.processViewportUpdate(viewport),
      updateThrottle
    );
  }

  /**
   * Update viewport with throttling
   */
  updateViewport(viewport: CameraViewport): boolean {
    this.totalUpdates++;
    
    // Check if we should throttle this update
    const now = Date.now();
    if (now - this.lastViewportUpdate < this.updateThrottle) {
      this.throttledUpdates++;
      // Still update via throttled function for eventual processing
      this.throttledUpdate(viewport);
      return false;
    }
    
    return this.processViewportUpdate(viewport);
  }

  /**
   * Process viewport update (after throttling)
   */
  private processViewportUpdate(viewport: CameraViewport): boolean {
    const now = Date.now();
    const timeSinceLastUpdate = this.currentViewport ? now - this.lastViewportUpdate : 0;
    
    this.currentViewport = { ...viewport };
    this.lastViewportUpdate = now;
    
    // Create event data
    const event: ViewportChangeEvent = {
      viewport: { ...viewport },
      timestamp: now,
      timeSinceLastUpdate,
    };
    
    this.emit('viewportChanged', event);
    
    return true;
  }

  /**
   * Force update viewport (bypass throttling)
   */
  forceUpdateViewport(viewport: CameraViewport): void {
    this.processViewportUpdate(viewport);
  }

  /**
   * Get current viewport
   */
  getCurrentViewport(): CameraViewport | null {
    return this.currentViewport ? { ...this.currentViewport } : null;
  }

  /**
   * Check if viewport has been stable for specified duration
   */
  isViewportStable(stabilityThreshold = TIMING.REMOVAL_STABILITY_MS): boolean {
    if (!this.currentViewport) {
      return false;
    }
    
    const now = Date.now();
    return now - this.lastViewportUpdate >= stabilityThreshold;
  }

  /**
   * Get time since last viewport update
   */
  getTimeSinceLastUpdate(): number {
    if (!this.currentViewport) {
      return 0;
    }
    
    return Date.now() - this.lastViewportUpdate;
  }

  /**
   * Compute visible tiles for current viewport and LOD level
   */
  computeVisibleTiles(
    lodLevel: LODLevel,
    metadata: any
  ): VisibleTilesResult {
    if (!this.currentViewport) {
      return {
        fovTiles: [],
        nonFovTiles: [],
        fovFaces: new Set(),
        totalTiles: 0,
      };
    }

    // Calculate visible tiles for higher detail loading (FOV faces)
    const visibleTiles = ViewportCalculator.getVisibleTiles(
      this.currentViewport,
      lodLevel,
      this.tilePadding
    );

    // Generate URLs using metadata configuration
    for (const tile of visibleTiles) {
      tile.url = UrlBuilder.build({
        x: tile.x,
        y: tile.y,
        face: tile.face,
        level: tile.level,
      }, metadata, [lodLevel]);
    }

    // Sort tiles by priority (higher priority first)
    visibleTiles.sort((a, b) => b.priority - a.priority);

    // Compute FOV faces from the currently visible tiles
    const fovFaceSet = new Set<string>(visibleTiles.map((t) => t.face));

    // First wave: only FOV faces (already in visibleTiles)
    const fovTiles = visibleTiles.filter((t) => fovFaceSet.has(t.face));

    // Second wave: other faces of the same target LOD level
    const otherFaces = ALL_CUBE_FACES.filter((f) => !fovFaceSet.has(f));
    const tilesPerFace = lodLevel.tilesPerFace;
    const nonFovTiles: TileInfo[] = [];
    
    for (const face of otherFaces) {
      for (let x = 0; x < tilesPerFace; x++) {
        for (let y = 0; y < tilesPerFace; y++) {
          const coord = { x, y, face: face as CubeFace, level: lodLevel.level };
          nonFovTiles.push({
            ...coord,
            url: UrlBuilder.build(coord, metadata, [lodLevel]),
            // Low priority since out of FOV
            priority: LOADING.OUT_OF_FOV_PRIORITY,
            isVisible: false,
            distance: 999,
          });
        }
      }
    }

    return {
      fovTiles,
      nonFovTiles,
      fovFaces: fovFaceSet,
      totalTiles: fovTiles.length + nonFovTiles.length,
    };
  }

  /**
   * Get adjacent tile coordinates with padding
   */
  getAdjacentTileCoordinates(
    coordinate: { x: number; y: number; face: string; level: number },
    tilesPerFace: number
  ): Array<{ x: number; y: number; face: string; level: number }> {
    const adjacent: Array<{ x: number; y: number; face: string; level: number }> = [];

    for (let dx = -this.tilePadding; dx <= this.tilePadding; dx++) {
      for (let dy = -this.tilePadding; dy <= this.tilePadding; dy++) {
        if (dx === 0 && dy === 0) continue; // Skip the tile itself

        const newX = coordinate.x + dx;
        const newY = coordinate.y + dy;

        // Check bounds
        if (newX >= 0 && newX < tilesPerFace && newY >= 0 && newY < tilesPerFace) {
          adjacent.push({
            x: newX,
            y: newY,
            face: coordinate.face,
            level: coordinate.level,
          });
        }
      }
    }

    return adjacent;
  }

  /**
   * Check if two viewports are significantly different
   */
  isViewportSignificantlyDifferent(
    viewport1: CameraViewport,
    viewport2: CameraViewport,
    fovThreshold = 5,
    angleThreshold = 5
  ): boolean {
    // Check FOV difference
    if (Math.abs(viewport1.fov - viewport2.fov) > fovThreshold) {
      return true;
    }

    // Check angle differences
    const horizontalAngleDiff = Math.abs(viewport1.horizontalAngle - viewport2.horizontalAngle);
    const verticalAngleDiff = Math.abs(viewport1.verticalAngle - viewport2.verticalAngle);

    return horizontalAngleDiff > angleThreshold || verticalAngleDiff > angleThreshold;
  }

  /**
   * Predict next viewport based on current movement (basic implementation)
   */
  predictNextViewport(deltaTime: number): CameraViewport | null {
    // This is a placeholder for more sophisticated viewport prediction
    // Could be enhanced with velocity tracking, acceleration, etc.
    return this.currentViewport;
  }

  /**
   * Get viewport statistics
   */
  getStats(): ViewportServiceStats {
    return {
      currentViewport: this.getCurrentViewport(),
      lastUpdateTime: this.lastViewportUpdate,
      timeSinceLastUpdate: this.getTimeSinceLastUpdate(),
      totalUpdates: this.totalUpdates,
      throttledUpdates: this.throttledUpdates,
      stabilizationTime: TIMING.REMOVAL_STABILITY_MS,
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.totalUpdates = 0;
    this.throttledUpdates = 0;
  }

  /**
   * Check if viewport is within FOV threshold for LOD level
   */
  isViewportInLODThreshold(viewport: CameraViewport, lodLevel: LODLevel): boolean {
    const threshold = lodLevel.fovThreshold;
    return viewport.fov >= threshold.min && viewport.fov <= threshold.max;
  }

  /**
   * Get optimal LOD level for current viewport
   */
  getOptimalLODLevel(lodLevels: LODLevel[]): LODLevel | null {
    if (!this.currentViewport) {
      return null;
    }
    
    return ViewportCalculator.calculateLODLevel(this.currentViewport.fov, lodLevels);
  }

  /**
   * Check if current viewport requires LOD change
   */
  requiresLODChange(currentLOD: LODLevel, lodLevels: LODLevel[]): boolean {
    const optimalLOD = this.getOptimalLODLevel(lodLevels);
    return optimalLOD !== null && optimalLOD.level !== currentLOD.level;
  }

  /**
   * Emit viewport stabilized event when viewport hasn't changed for threshold time
   */
  checkStabilization(): void {
    if (this.isViewportStable() && this.currentViewport) {
      this.emit('viewportStabilized', { ...this.currentViewport });
    }
  }

  /**
   * Add event listener
   */
  on<K extends keyof ViewportServiceEvents>(event: K, handler: ViewportServiceEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof ViewportServiceEvents>(event: K, handler: ViewportServiceEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event
   */
  private emit<K extends keyof ViewportServiceEvents>(
    event: K,
    ...args: Parameters<ViewportServiceEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          (handler as any)(...args);
        } catch (error) {
          // Silently handle event errors
        }
      }
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.currentViewport = null;
    this.eventHandlers.clear();
    this.resetStats();
  }
}
